import { addDoc, collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
	trackError,
	trackPurchaseEvent,
	ANALYTICS_EVENTS,
	ERROR_CONTEXTS,
} from './analytics.service';

/**
 * Creates a Stripe checkout session document in Firestore
 * The Stripe Extension watches this document and creates a Stripe session
 * It then populates the 'url' field with the hosted checkout URL
 *
 * @param {string} uid - User ID
 * @param {object} options
 *   - pendingPatternId: string (pattern ID)
 *   - priceId: string (Stripe price ID)
 *
 * @returns {Promise<string>} The Stripe checkout URL
 */
export const createPatternCheckoutSession = async (uid, options) => {
	const { pendingPatternId, priceId } = options;

	// Build the success and cancel URLs
	const origin = window.location.origin;
	const successUrl = `${origin}/pr?pid=${pendingPatternId}&session_id={CHECKOUT_SESSION_ID}`;
	const cancelUrl = origin; // Just redirect to home

	// Create the checkout session document
	const docRef = await addDoc(
		collection(db, `customers/${uid}/checkout_sessions`),
		{
			price: priceId,
			success_url: successUrl,
			cancel_url: cancelUrl,
			mode: 'payment',
			allow_promotion_codes: true,
		},
	);

	// Wait for Stripe Extension to populate the 'url' field
	return new Promise((resolve, reject) => {
		const unsubscribe = onSnapshot(
			doc(db, `customers/${uid}/checkout_sessions`, docRef.id),
			(snapshot) => {
				const data = snapshot.data();

				// If extension encounters an error
				if (data?.error) {
					unsubscribe();
					const err = new Error(
						data.error.message || 'Stripe session creation failed',
					);
					trackError(ERROR_CONTEXTS.CREATE_CHECKOUT_SESSION, err, {
						pendingPatternId,
					});
					reject(err);
					return;
				}

				// If URL is ready, return it
				if (data?.url) {
					unsubscribe();
					trackPurchaseEvent(
						ANALYTICS_EVENTS.CHECKOUT_SESSION_CREATED,
						{ pendingPatternId },
					);
					resolve(data.url);
					return;
				}
			},
			(error) => {
				unsubscribe();
				reject(error);
			},
		);
	});
};
